import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Megaphone, Users, Building2, UserCog, Baby, Mail, MessageCircle,
  Upload, Copy, Send, ChevronDown, ChevronRight, Loader2, CheckCircle2,
  XCircle, Sparkles, FileText, RefreshCw, Trash2, Eye, X, Save, RotateCcw,
} from "lucide-react";

const AUDIENCES = [
  { id: "clinica",       label: "Clínicas",       icon: Building2, color: "#0B2B62",
    desc: "Convite pra clínicas se tornarem Unidade Parceira RAF" },
  { id: "profissional",  label: "Profissionais",  icon: UserCog,   color: "#059669",
    desc: "Convite pra profissionais se credenciarem (videoconsulta)" },
  { id: "consultor",     label: "Consultores",    icon: Sparkles,  color: "#7C3AED",
    desc: "Convite pra consultores se candidatarem (10% da matrícula)" },
  { id: "familia",       label: "Famílias",       icon: Baby,      color: "#DC2626",
    desc: "Convite pra famílias fazerem triagem gratuita" },
];

const OutreachCampaign = () => {
  const [audience, setAudience] = useState("clinica");
  const [channels, setChannels] = useState(["email"]);
  const [templates, setTemplates] = useState({});
  const [templateId, setTemplateId] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [sending, setSending] = useState(false);
  const [forceFreeform, setForceFreeform] = useState(false);
  const [history, setHistory] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [rowDetail, setRowDetail] = useState({});
  const [preview, setPreview] = useState(null);          // { html, subject } ou null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplBaseline, setTplBaseline] = useState({ subject: "", body: "" });
  const fileRef = useRef(null);

  useEffect(() => { api.get("/admin/outreach/templates").then(r => setTemplates(r.data)); }, []);
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    // Auto-selecionar primeiro template ao trocar audiência
    const list = templates[audience] || [];
    if (list.length && !list.find(t => t.id === templateId)) {
      applyTemplate(list[0]);
    }
    setLeads([]);   // limpa leads antigos
  }, [audience, templates]);

  const applyTemplate = (t) => {
    setTemplateId(t.id);
    setSubject(t.subject);
    setBody(t.body);
    setTplBaseline({ subject: t.subject, body: t.body });
  };

  const isTplDirty = useMemo(() => {
    return (subject || "") !== (tplBaseline.subject || "") ||
           (body || "") !== (tplBaseline.body || "");
  }, [subject, body, tplBaseline]);

  const currentTemplateMeta = useMemo(() => {
    const list = templates[audience] || [];
    return list.find(t => t.id === templateId);
  }, [templates, audience, templateId]);

  const saveTemplate = async () => {
    if (!templateId) { toast.error("Selecione um template primeiro"); return; }
    if (!isTplDirty) { toast.info("Nenhuma alteração para salvar"); return; }
    setSavingTpl(true);
    try {
      await api.put(`/admin/outreach/templates/${templateId}`, {
        subject: subject.trim(),
        body: body.trim(),
      });
      // Recarrega o merge de templates atualizado do servidor
      const { data } = await api.get("/admin/outreach/templates");
      setTemplates(data);
      setTplBaseline({ subject, body });
      toast.success("Template salvo — vai ser usado nas próximas campanhas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar template");
    } finally { setSavingTpl(false); }
  };

  const resetTemplate = async () => {
    if (!templateId) return;
    if (!window.confirm("Descartar a edição salva e voltar ao texto original?")) return;
    setSavingTpl(true);
    try {
      await api.delete(`/admin/outreach/templates/${templateId}`);
      const { data } = await api.get("/admin/outreach/templates");
      setTemplates(data);
      const fresh = (data[audience] || []).find(t => t.id === templateId);
      if (fresh) {
        setSubject(fresh.subject);
        setBody(fresh.body);
        setTplBaseline({ subject: fresh.subject, body: fresh.body });
      }
      toast.success("Template restaurado ao original");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar template");
    } finally { setSavingTpl(false); }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/outreach/history");
      setHistory(data || []);
    } catch { /* silent */ }
  };

  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      const { data } = await api.get(`/admin/outreach/leads?audience=${audience}`);
      setLeads(data.leads || []);
      toast.success(`${data.total} lead(s) encontrados`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao buscar leads");
    } finally { setLoadingLeads(false); }
  };

  // Parser de texto colado: 1 destinatário por linha, aceita "nome, email, telefone"
  // ou só o campo isolado.
  const parsePaste = () => {
    const rows = pasteText.split(/[\n\r]+/).map(l => l.trim()).filter(Boolean);
    const parsed = rows.map(line => {
      // separar por vírgula, ponto-e-vírgula ou tab
      const parts = line.split(/[,;\t]/).map(p => p.trim());
      let name = "", email = "", phone = "";
      parts.forEach(p => {
        if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p)) email = p;
        else if (/^\+?\d[\d\s()+-]{7,}$/.test(p)) phone = p;
        else if (!name) name = p;
      });
      return { name, email, phone };
    }).filter(r => r.email || r.phone);
    if (!parsed.length) { toast.error("Nenhum e-mail/telefone válido detectado"); return; }
    setRecipients(prev => dedupeRecipients([...prev, ...parsed]));
    setPasteText("");
    toast.success(`${parsed.length} destinatário(s) adicionado(s)`);
  };

  const dedupeRecipients = (list) => {
    const seen = new Set();
    return list.filter(r => {
      const key = (r.email || "").toLowerCase() + "|" + (r.phone || "");
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });
  };

  const onCSV = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/outreach/parse-csv", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRecipients(prev => dedupeRecipients([...prev, ...(data.recipients || [])]));
      toast.success(`${data.count} linha(s) importada(s) do CSV`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao processar CSV");
    }
    fileRef.current.value = "";
  };

  const importLeads = () => {
    if (!leads.length) { toast.error("Nenhum lead disponível — clique em Buscar Leads primeiro"); return; }
    setRecipients(prev => dedupeRecipients([...prev, ...leads]));
    toast.success(`${leads.length} lead(s) importado(s)`);
  };

  const removeRecipient = (idx) => setRecipients(recipients.filter((_, i) => i !== idx));
  const clearAllRecipients = () => setRecipients([]);

  const validRecipientsCount = useMemo(() => {
    return recipients.filter(r => {
      if (channels.includes("email") && r.email) return true;
      if (channels.includes("whatsapp") && r.phone) return true;
      return false;
    }).length;
  }, [recipients, channels]);

  const send = async () => {
    if (!channels.length) { toast.error("Escolha ao menos 1 canal"); return; }
    if (channels.includes("email") && !subject.trim()) { toast.error("Assunto vazio"); return; }
    if (!body.trim()) { toast.error("Corpo vazio"); return; }
    if (!recipients.length) { toast.error("Adicione destinatários"); return; }
    if (!window.confirm(`Confirmar envio pra ${recipients.length} destinatário(s) via ${channels.join("+")}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/outreach/send", {
        audience, channels, subject, body, recipients, template_id: templateId,
        force_freeform: forceFreeform,
      });
      toast.success(`Enviado! ${data.stats.email_sent} email · ${data.stats.wa_sent} WA`, { duration: 6000 });
      setRecipients([]);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio");
    } finally { setSending(false); }
  };

  const openPreview = async () => {
    if (!body.trim()) { toast.error("Escreva a mensagem antes"); return; }
    setPreviewLoading(true);
    try {
      const sampleName = recipients[0]?.name || "Maria";
      const { data } = await api.post("/admin/outreach/preview", {
        subject, body, sample_name: sampleName,
      });
      setPreview(data);
    } catch (e) {
      toast.error("Erro ao gerar prévia");
    } finally { setPreviewLoading(false); }
  };

  const toggleRow = async (id) => {
    if (expandedRow === id) { setExpandedRow(null); return; }
    setExpandedRow(id);
    if (!rowDetail[id]) {
      try {
        const { data } = await api.get(`/admin/outreach/campaign/${id}`);
        setRowDetail(prev => ({ ...prev, [id]: data }));
      } catch { /* silent */ }
    }
  };

  const currentTemplates = templates[audience] || [];

  return (
    <div className="space-y-6" data-testid="outreach-campaign-panel">
      <div className="flex items-center gap-2">
        <Megaphone className="w-6 h-6 text-[#0B2B62]" />
        <h2 className="font-display font-bold text-2xl text-[#0B2B62]">Divulgação · Convites em massa</h2>
      </div>

      {/* 1) Audiência */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">1. Público-alvo</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {AUDIENCES.map(a => {
            const Icon = a.icon;
            const active = audience === a.id;
            return (
              <button key={a.id} onClick={() => setAudience(a.id)}
                      data-testid={`outreach-audience-${a.id}`}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${
                        active ? "border-[#0B2B62] bg-white shadow-md" : "border-slate-200 bg-white/60 hover:border-slate-300"
                      }`}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: a.color }} />
                  <span className="font-display font-bold text-sm text-[#0B2B62]">{a.label}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-tight">{a.desc}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* 2) Canal */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">2. Canal</div>
        <div className="flex gap-2">
          {[{ id: "email", label: "E-mail", icon: Mail },
             { id: "whatsapp", label: "WhatsApp", icon: MessageCircle }].map(c => {
            const Ic = c.icon;
            const active = channels.includes(c.id);
            return (
              <button key={c.id} onClick={() => setChannels(active ? channels.filter(x => x !== c.id) : [...channels, c.id])}
                      data-testid={`outreach-channel-${c.id}`}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 text-sm font-semibold transition-all ${
                        active ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}>
                <Ic className="w-4 h-4" /> {c.label}
                {active && <CheckCircle2 className="w-3.5 h-3.5" />}
              </button>
            );
          })}
        </div>
        {channels.includes("whatsapp") && (
          <div className="mt-2 p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-[11px] text-emerald-900 leading-relaxed" data-testid="wa-24h-warning">
            <b>💚 WhatsApp Business em produção com template aprovado</b>
            {audience === "familia" && (
              <span> · Audiência <b>Família</b> usa template <code>convite_familia</code> — funciona FORA da janela 24h. Variáveis: {`{{1}}=nome, {{2}}=cidade`}.</span>
            )}
            {audience === "clinica" && (
              <span> · Audiência <b>Clínica</b> usa template <code>convite_clinica</code> — funciona FORA da janela 24h. Variáveis: {`{{1}}=nome, {{2}}=cidade`}.</span>
            )}
            {audience === "consultor" && (
              <span> · Audiência <b>Consultor</b> usa template <code>convite_consultor</code> — funciona FORA da janela 24h. Variáveis: {`{{1}}=nome, {{2}}=cidade`}.</span>
            )}
            <br />
            <span className="text-emerald-800">💡 Dica: preencha o campo <b>cidade</b> no CSV/leads pra personalização — se vazio, vai como "sua região".</span>
            <label className="mt-2 flex items-start gap-2 pt-2 border-t border-emerald-200 cursor-pointer" data-testid="wa-freeform-toggle">
              <input type="checkbox" checked={forceFreeform}
                     onChange={(e) => setForceFreeform(e.target.checked)}
                     data-testid="wa-freeform-checkbox"
                     className="mt-0.5" />
              <span className="text-emerald-900">
                <b>Enviar como texto livre</b> (destinatário já falou comigo nas últimas 24h)
                <br />
                <span className="text-[10px] text-emerald-800/80">
                  Use isto para <b>testes com seu próprio número</b> ou quando templates estão pendentes/rejeitados no Meta. Só entrega se a janela de 24h estiver aberta.
                </span>
              </span>
            </label>
          </div>
        )}
      </section>

      {/* 3) Template + editor */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center justify-between">
          <span>3. Mensagem (edite à vontade)</span>
          <span className="text-slate-400">Variáveis: <code className="bg-slate-100 px-1">{"{{nome}}"}</code></span>
        </div>
        <div className="flex gap-2 flex-wrap mb-3">
          {currentTemplates.map(t => (
            <button key={t.id} onClick={() => applyTemplate(t)}
                    data-testid={`outreach-template-${t.id}`}
                    className={`text-xs px-3 py-1.5 rounded-md border relative ${
                      templateId === t.id ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}>
              <FileText className="w-3 h-3 inline mr-1" /> {t.title}
              {t.edited && (
                <span className={`ml-1 text-[9px] font-bold px-1 rounded ${templateId === t.id ? "bg-emerald-400 text-emerald-900" : "bg-emerald-100 text-emerald-800"}`}
                      title={`Editado por você em ${(t.edited_at || "").slice(0, 16).replace("T", " ")}`}>
                  EDITADO
                </span>
              )}
            </button>
          ))}
        </div>
        {channels.includes("email") && (
          <input value={subject} onChange={(e) => setSubject(e.target.value)}
                 data-testid="outreach-subject"
                 placeholder="Assunto do e-mail"
                 className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500 mb-2" />
        )}
        <textarea value={body} onChange={(e) => setBody(e.target.value)}
                  data-testid="outreach-body" rows={9}
                  placeholder="Corpo da mensagem..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500 font-mono" />
        <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
          <div className="text-[10px] text-slate-500">{body.length} caracteres · e-mail sem limite · WhatsApp evite &gt;1600</div>
          <div className="flex items-center gap-2">
            {isTplDirty && (
              <span className="text-[10px] text-amber-700 font-bold uppercase tracking-widest" data-testid="tpl-dirty-indicator">
                • Alterações não salvas
              </span>
            )}
            {currentTemplateMeta?.edited && !isTplDirty && (
              <button type="button" onClick={resetTemplate} disabled={savingTpl}
                      data-testid="outreach-tpl-reset"
                      className="text-xs px-2.5 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
                <RotateCcw className="w-3 h-3" /> Restaurar original
              </button>
            )}
            <button type="button" onClick={saveTemplate} disabled={savingTpl || !isTplDirty || !templateId}
                    data-testid="outreach-tpl-save"
                    className="text-xs px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-bold inline-flex items-center gap-1">
              {savingTpl ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Salvar edição
            </button>
          </div>
        </div>
      </section>

      {/* 4) Destinatários */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">4. Destinatários</div>

        {/* 3 sub-blocos: colar, csv, leads */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="p-3 rounded-lg border border-slate-200 bg-white">
            <div className="text-xs font-bold text-slate-700 mb-1">Colar lista</div>
            <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                      data-testid="outreach-paste-textarea" rows={3}
                      placeholder="1 por linha: nome, email, telefone"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs font-mono" />
            <button onClick={parsePaste} disabled={!pasteText.trim()}
                    data-testid="outreach-paste-btn"
                    className="mt-2 text-xs px-3 py-1 bg-[#0B2B62] hover:bg-[#08204a] disabled:opacity-50 text-white rounded-md">
              Adicionar da lista
            </button>
          </div>

          <div className="p-3 rounded-lg border border-slate-200 bg-white">
            <div className="text-xs font-bold text-slate-700 mb-1">Upload CSV</div>
            <p className="text-[10px] text-slate-500 mb-1">Cabeçalhos: nome, email, telefone (,/;)</p>
            <input type="file" accept=".csv,.txt" ref={fileRef}
                   data-testid="outreach-csv-input"
                   onChange={(e) => onCSV(e.target.files?.[0])}
                   className="text-xs w-full" />
          </div>

          <div className="p-3 rounded-lg border border-slate-200 bg-white">
            <div className="text-xs font-bold text-slate-700 mb-1">Leads da base</div>
            <p className="text-[10px] text-slate-500 mb-2">
              {audience === "clinica"       && "Clínicas não publicadas"}
              {audience === "profissional"  && "Profissionais sem credenciamento"}
              {audience === "consultor"     && "Consultores + candidaturas"}
              {audience === "familia"       && "Famílias cadastradas"}
            </p>
            <div className="flex gap-1">
              <button onClick={fetchLeads} disabled={loadingLeads}
                      data-testid="outreach-fetch-leads-btn"
                      className="text-xs px-2 py-1 bg-white border border-emerald-500 text-emerald-700 rounded-md hover:bg-emerald-50 disabled:opacity-50">
                {loadingLeads ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <RefreshCw className="w-3 h-3 inline" />} Buscar
              </button>
              <button onClick={importLeads} disabled={!leads.length}
                      data-testid="outreach-import-leads-btn"
                      className="text-xs px-2 py-1 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white rounded-md">
                Importar {leads.length ? `(${leads.length})` : ""}
              </button>
            </div>
          </div>
        </div>

        {/* Lista atual de destinatários */}
        {!!recipients.length && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold text-slate-700">
                Lista atual: <b>{recipients.length}</b> destinatário(s) ·
                <span className="text-emerald-700 ml-1">{validRecipientsCount} elegível(is)</span>
              </div>
              <button onClick={clearAllRecipients}
                      data-testid="outreach-clear-recipients-btn"
                      className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Limpar
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {recipients.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-white px-2 py-1 rounded border border-slate-100">
                  <span className="flex-1 truncate">
                    <b>{r.name || "—"}</b>
                    {r.email && <span className="text-slate-500 ml-2">✉ {r.email}</span>}
                    {r.phone && <span className="text-slate-500 ml-2">📱 {r.phone}</span>}
                  </span>
                  <button onClick={() => removeRecipient(i)}
                          data-testid={`outreach-remove-recipient-${i}`}
                          className="text-red-500 hover:text-red-700">
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 5) Enviar */}
      <section className="pt-4 border-t border-slate-200 flex flex-wrap items-center gap-3">
        <button onClick={openPreview} disabled={previewLoading || !body.trim()}
                data-testid="outreach-preview-btn"
                className="inline-flex items-center gap-2 px-4 py-3 border-2 border-[#0B2B62] hover:bg-blue-50 disabled:opacity-50 text-[#0B2B62] font-bold text-sm rounded-md">
          {previewLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Gerando prévia…</> :
                            <><Eye className="w-4 h-4" /> Ver prévia do e-mail</>}
        </button>
        <button onClick={send} disabled={sending || !recipients.length}
                data-testid="outreach-send-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-bold text-sm rounded-md">
          {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</> :
                     <><Send className="w-4 h-4" /> Disparar campanha ({validRecipientsCount})</>}
        </button>
        <span className="text-[11px] text-slate-500">A prévia mostra exatamente como o destinatário vai receber (com HTML renderizado + link clicável).</span>
      </section>

      {/* Histórico */}
      <section className="pt-6 border-t border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg text-[#0B2B62]">Histórico de campanhas</h3>
          <button onClick={loadHistory} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Atualizar
          </button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Nenhuma campanha ainda.</p>
        ) : (
          <div className="space-y-2" data-testid="outreach-history">
            {history.map(h => {
              const s = h.stats || {};
              return (
                <div key={h.id} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                  <button onClick={() => toggleRow(h.id)}
                          data-testid={`outreach-history-row-${h.id}`}
                          className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50">
                    {expandedRow === h.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0B2B62] truncate">
                        {h.audience.toUpperCase()} · {(h.channels || []).join("+")} · {h.subject || "(sem assunto)"}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {(h.started_at || "").slice(0, 16).replace("T", " ")}
                        {" · "}✉ {s.email_sent || 0} {s.email_failed ? `(${s.email_failed} falhas)` : ""}
                        {" · "}📱 {s.wa_sent || 0} {s.wa_failed ? `(${s.wa_failed} falhas)` : ""}
                      </div>
                    </div>
                  </button>
                  {expandedRow === h.id && rowDetail[h.id] && (
                    <div className="border-t border-slate-100 bg-slate-50 p-3 space-y-1 max-h-64 overflow-y-auto">
                      {(rowDetail[h.id].recipients || []).map((r, i) => (
                        <div key={i} className="text-[11px] text-slate-700 flex items-start gap-2 py-0.5" data-testid={`outreach-recipient-${h.id}-${i}`}>
                          <span className="flex-1 min-w-0 truncate">{r.name || "—"} · {r.email || r.phone}</span>
                          {r.email_status && <span className={`whitespace-nowrap ${r.email_status === "sent" ? "text-emerald-700" : "text-red-600"}`} title={r.email_status}>✉ {r.email_status.slice(0, 40)}</span>}
                          {r.wa_status && <span className={`flex-1 min-w-0 truncate ${String(r.wa_status).startsWith("sent") ? "text-emerald-700" : "text-red-600"}`} title={r.wa_status} data-testid={`outreach-wa-status-${h.id}-${i}`}>📱 {r.wa_status}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Modal de prévia — mostra exatamente o e-mail que o destinatário vai
          receber (mesmo wrap HTML usado no envio real). Corpo do WhatsApp é
          exibido logo abaixo em plain-text porque WA não aceita HTML. */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => setPreview(null)}
             data-testid="outreach-preview-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Prévia do envio</div>
                <div className="font-display font-bold text-[#0B2B62]">
                  {channels.includes("email") ? preview.subject || "(sem assunto)" : "Corpo do WhatsApp"}
                </div>
              </div>
              <button onClick={() => setPreview(null)}
                      data-testid="outreach-preview-close"
                      className="p-2 rounded hover:bg-slate-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {channels.includes("email") && (
                <iframe title="Prévia e-mail"
                        data-testid="outreach-preview-iframe"
                        srcDoc={preview.html}
                        className="w-full h-[500px] border-0"
                        sandbox="allow-same-origin" />
              )}
              {channels.includes("whatsapp") && (
                <div className="p-4 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                    Como aparece no WhatsApp
                  </div>
                  <div className="max-w-sm bg-[#DCF8C6] rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap font-normal"
                       style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}
                       data-testid="outreach-preview-whatsapp">
                    {body.replace(/\{\{(?:nome|name)\}\}/g, recipients[0]?.name || "Maria")}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2">O WhatsApp detecta URLs sozinho e transforma em link clicável no celular.</p>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
              <span>Prévia com nome-exemplo: <b>{recipients[0]?.name || "Maria"}</b></span>
              <button onClick={() => setPreview(null)} className="text-slate-600 hover:text-slate-800">Fechar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OutreachCampaign;
